import React, { FC, RefObject, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Table } from '@components/atoms/Table';
import { Card } from '@components/atoms/Card';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Form } from '@components/atoms/Form';
import { Pagination } from '@components/molecules/Pagination';
import { Select, SelectType } from '@components/atoms/Select';
import { CouponStateBadge } from '@components/molecules/badges/CouponStateBadge';
import { PopupDelete } from '@components/molecules/PopupDelete';
import { SITE_URLS } from '@constants';
import { useGetAllMyAccountStores } from '@services/useGetAllMyAccountStores';
import { useGetMyCoupons } from '@services/useGetMyCoupons';
import { useDeleteCoupon } from '@services/useDeleteCoupon';
import { usePermissionChecker, UserPermissions } from '@hooks/useHasPermission';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { useFilters } from '@hooks/useFilters';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useNotifications } from '@hooks/useNotifications';
import { formatDate } from '@utils/format';
import { ResultsType } from '@types';
import styles from './MyCoupons.module.css';
import { Modules, useModule } from '@hooks/useModule';

const STORE_FILTER_ID = 'store-filter';

export const MyCoupons: FC = () => {
    const { navigate } = useCustomNavigate();
    const { can } = usePermissionChecker();
    const { isModuleActive } = useModule();
    const { pageToRequest, resetPageToRequest, checkPageToRequest, observerRef: scrollObserverRef } = useInfiniteScroll();
    const { t } = useTranslation();

    const translations = {
        title: t('my-coupons-component.title'),
        searchPlaceholder: t('my-coupons-component.search.placeholder'),
    };

    // if user does not have permission accessMerchantAreas, he can't access this page
    useEffect(() => {
        if (can(UserPermissions.accessMerchantAreas) === false || !isModuleActive(Modules.coupon)) {
            navigate(`/${SITE_URLS.myProfile}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formRef = useRef<HTMLFormElement>(null);

    return (
        <MyAccountContainer title={translations.title}>
            <>
                <div className="pt-4 pb-2">
                    <Filters formRef={formRef} pageToRequest={pageToRequest} checkPageToRequest={checkPageToRequest} />
                </div>
                <div className="pt-4">
                    <CouponsTable formRef={formRef} resetPageToRequest={resetPageToRequest} />
                    <div ref={scrollObserverRef} style={{ height: '1px' }} />
                </div>
            </>
        </MyAccountContainer>
    );
};

interface FiltersProps {
    formRef: RefObject<HTMLFormElement>;
    pageToRequest: number;
    checkPageToRequest: () => void;
}

const Filters: FC<FiltersProps> = ({ formRef, pageToRequest, checkPageToRequest }) => {
    const location = useLocation();
    const pageSize = useMediaQuery();
    const { request: requestMyCoupons } = useGetMyCoupons();
    const { myCoupons, isLoadingMyCoupons, setMyCoupons } = useData();
    const { appliedFilters, results, setResults, currentPage, setCurrentPage, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        addCoupon: t('my-coupons-component.add-coupon'),
    };

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this page
        return () => {
            setResults([]);
            setMyCoupons(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let getStoreFilter = appliedFilters.find((item) => item.label === STORE_FILTER_ID)?.values.map((value) => value.name)?.[0];

    useEffect(() => {
        // check if url states a specific store, initially
        const queryParams = new URLSearchParams(location.search);
        const getStoreParam = queryParams.get('store') || '';
        if (getStoreParam) {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            getStoreFilter = getStoreParam;
            setFilterValues(STORE_FILTER_ID, [{ uuid: null, name: getStoreParam }]);
        }
    }, []);

    const fetchCoupons = (page: number) => {
        setCurrentPage(page);

        // prevent calls for pages that don't exist
        if (myCoupons?.pagination?.total_pages && page > myCoupons?.pagination?.total_pages) {
            return;
        }

        requestMyCoupons({
            stores_uuid: getStoreFilter,
            page: page,
        });
    };

    // request more coupons from next page
    useEffect(() => {
        pageToRequest > currentPage && !isLoadingMyCoupons && fetchCoupons(currentPage + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageToRequest, isLoadingMyCoupons]);

    // request first page whenever a filter changes
    const submitForm = () => fetchCoupons(1);

    useEffect(() => {
        !isLoadingMyCoupons && formRef?.current?.requestSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters]);

    // every time the endpoint is called, append the data to results constant
    // if the page being called is the first page, reset results
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setResults((previous: any[]) => {
            return myCoupons?.pagination?.current_page === 1 ? myCoupons?.data || [] : [...previous, ...(myCoupons?.data || [])];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myCoupons]);

    // manually check if next page should be requested
    // in case user's screen is big and isn't possible to scroll to trigger next page request
    useEffect(() => {
        checkPageToRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results.length]);

    return (
        <MyAccountContainer.Filters>
            <Form formRef={formRef} onSubmit={submitForm}>
                <div className={`d-flex flex-column flex-md-row ${styles['grouped-select-filters']}`}>
                    <StoreFilter />
                </div>
                {/* add coupon */}
                <div>
                    <Anchor
                        href={SITE_URLS.myCouponsCreate}
                        classes={['xs', 'sm'].includes(pageSize) ? 'w-100' : ''}
                        mode={AnchorMode.primary}>
                        {translations.addCoupon}
                    </Anchor>
                </div>
            </Form>
        </MyAccountContainer.Filters>
    );
};

interface CouponsTableProps {
    formRef: RefObject<HTMLFormElement>;
    resetPageToRequest: () => void;
}

const CouponsTable: FC<CouponsTableProps> = ({ formRef, resetPageToRequest }) => {
    const { showDefaultNotification } = useNotifications();
    const { request: requestDelete, loading: loadingDelete, error: errorDelete, lastResponseTime } = useDeleteCoupon();
    const { results: coupons } = useFilters();
    const { isLoadingMyCoupons } = useData();
    const { t } = useTranslation();

    const translations = {
        promoCode: t('my-coupons-component.table.promo-code'),
        store: t('my-coupons-component.table.store'),
        discount: t('my-coupons-component.table.discount'),
        value: t('my-coupons-component.table.value'),
        startDate: t('my-coupons-component.table.start-date'),
        endDate: t('my-coupons-component.table.end-date'),
        state: t('my-coupons-component.table.state'),
        used: t('my-coupons-component.table.used'),
        actions: t('my-coupons-component.table.actions'),
        deletePopupTitle: t('my-coupons-component.delete-popup.title'),
        deletePopupDescription: t('my-coupons-component.delete-popup.description'),
        deletePopupWarning: t('my-coupons-component.delete-popup.warning'),
        successfullyDeleted: t('my-coupons-component.delete-success'),
    };

    // uuid of coupon being removed
    const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);

    // dispatch notification, according to feedback of service
    useEffect(() => {
        if (lastResponseTime) {
            // close delete modal
            showDeleteModal !== null && setShowDeleteModal(null);

            if (errorDelete) {
                showDefaultNotification({ type: 'error', message: errorDelete });
            } else {
                showDefaultNotification({ type: 'success', message: translations.successfullyDeleted });
                // reset paginated list of products
                formRef?.current?.requestSubmit();
                resetPageToRequest();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const handleOnConfirmDelete: () => void = () => {
        showDeleteModal && requestDelete({ uuid: showDeleteModal });
    };

    return (
        <div data-testid="management-table">
            <Card classes="mb-0">
                <Table
                    borderless={true}
                    headerCols={[
                        { classes: 'text-left', text: translations.promoCode },
                        { classes: 'text-left', hideOnMobile: true, text: translations.store },
                        { classes: 'text-center', text: translations.discount },
                        { classes: 'text-center', hideOnMobile: true, text: translations.startDate },
                        { classes: 'text-center', hideOnMobile: true, text: translations.endDate },
                        { classes: 'text-center', text: translations.state },
                        { classes: 'text-center', text: translations.used },
                        { classes: 'text-end', fit: true, text: translations.actions },
                    ]}
                    contentRows={coupons.map((coupon) => {
                        return [
                            {
                                classes: 'text-left text-muted',
                                content: <Typography fontSize="14">{coupon.promo_code}</Typography>,
                            },
                            {
                                classes: 'text-left',
                                content: <Typography fontSize="14">{coupon.store}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{coupon.discount}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{formatDate(coupon.start_date)}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{formatDate(coupon.end_date)}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <CouponStateBadge state={coupon.status} />,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{coupon.used}</Typography>,
                            },
                            {
                                classes: 'text-end',
                                content: (
                                    <Table.Options
                                        options={[
                                            <Anchor
                                                key="option-1"
                                                href={SITE_URLS.myCouponsEdit.replace(':uuid', `${coupon.uuid}`)}
                                                mode={AnchorMode.none}>
                                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                    <i className="fa-light fa-pen-to-square" />
                                                </Typography>
                                            </Anchor>,
                                            <button
                                                key="option-2"
                                                disabled={loadingDelete}
                                                onClick={() => setShowDeleteModal(coupon.uuid || null)}>
                                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                    <i className="fa-light fa-trash-can" />
                                                </Typography>
                                            </button>,
                                        ]}
                                    />
                                ),
                            },
                        ];
                    })}
                />
            </Card>
            <Pagination loading={isLoadingMyCoupons} type={ResultsType.promotions} />

            {/* popup to confirm deletion of variant */}
            <PopupDelete
                question={translations.deletePopupTitle}
                confirmation={translations.deletePopupDescription}
                warning={translations.deletePopupWarning}
                onClickConfirm={handleOnConfirmDelete}
                show={showDeleteModal !== null}
                onHide={() => setShowDeleteModal(null)}
            />
        </div>
    );
};

const StoreFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const { request: requestAllMyAccountStores } = useGetAllMyAccountStores();
    const { allMyAccountStores, isLoadingAllMyAccountStores, isLoadingMyAccountProducts } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('my-coupons-component.store.label'),
        default: t('select-default-option'),
    };

    // call services, if we don't have the information yet
    useEffect(() => {
        !allMyAccountStores && !isLoadingAllMyAccountStores && requestAllMyAccountStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMyAccountStores]);

    const options = [
        { value: '', text: translations.default },
        ...(allMyAccountStores?.data || []).map((item) => {
            return { value: item.uuid || '', text: item.name || '' };
        }),
    ];

    const selected = appliedFilters.find((item) => item.label === STORE_FILTER_ID)?.values.map((value) => value.name)?.[0];

    const handleOnChange: (value: string) => void = (value) => setFilterValues(STORE_FILTER_ID, [{ uuid: null, name: value }]);

    return (
        <div className="d-flex align-items-center">
            <Typography classes="text-nowrap mb-0 me-2" fontSize="16">
                {translations.title}
            </Typography>
            <Select
                classes={['xs', 'sm'].includes(pageSize) ? 'flex-grow-1' : ''}
                width={['xs', 'sm'].includes(pageSize) ? '100%' : '146px'}
                type={SelectType.variant2}
                options={options}
                disabled={allMyAccountStores === null || isLoadingAllMyAccountStores || isLoadingMyAccountProducts}
                value={selected}
                onChange={handleOnChange}
            />
        </div>
    );
};
